import React, { useState, useEffect } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Table, Input, Select, Modal, Tooltip, Tag, DatePicker, Form, Switch, notification, InputNumber, Drawer, Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    LoadingOutlined,
    ReloadOutlined,
    CarOutlined,
    CheckOutlined,
    QuestionCircleOutlined,
    EyeFilled,
    UnorderedListOutlined,
} from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import CargoDetails from '@flc/components/logistics/cargo-details/cargo-details';

import { exception, securityRestriction, error } from '@extensions/notification';
import { getDateTimeLocal } from '@extensions/utils';

import { useAppDispatch, useAppSelector } from '@store/hooks';
import { userLoaded, setFilter } from '@store/actions';
import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';
import { ITruckFilter } from '@entities/truck-filter';
import { getEnumList, delayAction, toFinanceString } from '@extensions/utils';
import { ITruck } from '@entities/truck';
import { ICity } from '@entities/city';
import { ICountry } from '@entities/country';

import { Permission, hasPermission } from '@enums/permission';
import { TruckStatus, enumLabel as truckStatusLabel } from '@enums/truck-status';
import { IEnumItem } from '@enums/enum-item';

dayjs.extend(utc);

const filterContext: string = 'DeliveryTrucks';

const Trucks = () => {
    const initFilter: ITruckFilter = {
        statuses: [TruckStatus.New, TruckStatus.OnWay],
        isArchived: false,
    };

    const { RangePicker } = DatePicker;

    const navigate = useNavigate();
    const resolved = useResolvedPath('');

    const [api, notificationContextHolder] = notification.useNotification();
    const [modal, contextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);
    const filter = useAppSelector<ITruckFilter>((s) => s.filters[filterContext]);

    const [trucks, setTrucks] = useState<Array<ITruck>>([]);
    const [currentTruck, setCurrentTruck] = useState<ITruck>();
    const [countries, setCountries] = useState<Array<ICountry>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);

    const [statuses] = useState<Array<IEnumItem>>(getEnumList(TruckStatus, truckStatusLabel));

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [openDetails, setOpenDetails] = useState<boolean>(false);

    useEffect(() => {
        let cleanup = false;

        if (!refreshRequired) return;

        setSelectedIds([]);

        const fetchData = async () => {
            setLoading(true);

            const promises = [
                await serverFetch('countries', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('cities', { method: 'GET' })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения городов', ex, () => d(userLoaded(undefined)));
                    }),

                await serverFetch('trucks/delivery', { method: 'GET', queryParams: filter })
                    .then((data) => {
                        return data;
                    })
                    .catch((ex) => {
                        exception(api, 'Ошибка получения машин', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setCountries(result[0][0] || []);
                setCities(result[0][1] || []);
                setTrucks(result[0][2] || []);

                setLoading(false);
                setRefreshRequired(false);
            });
        };

        fetchData();

        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    const onFinish = () => {
        setLoading(true);

        const journalIds: Array<string> = [];

        trucks.map((t: ITruck) => {
            if (t.id && selectedIds.includes(t.id) && t.journalId) {
                journalIds.push(t.journalId);
            }
        });

        serverFetch(`trucks/delivery/finish`, { method: 'POST', bodyData: journalIds })
            .then(() => {
                setLoading(false);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка завершения доставки', ex, () => d(userLoaded(undefined)));
            });
    };

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));

            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Управление машиной',
                        key: 'add',
                        disabled: !currentTruck,
                        icon: <CarOutlined />,
                        onClick: () => {
                            if (currentTruck?.id) {
                                navigate(`${resolved.pathname}/${currentTruck.id}/${currentTruck?.journalId}`);
                            } else {
                                navigate(`${resolved.pathname}/new/${currentTruck?.journalId}`);
                            }
                        },
                    },
                    {
                        label: 'Детали доставки',
                        key: 'consignmentDetails',
                        disabled: !currentTruck,
                        icon: <UnorderedListOutlined />,
                        onClick: () => {
                            setOpenDetails(true);
                        },
                    },
                    { isDivider: true },
                    {
                        label: 'Завершить доставку',
                        key: 'complete',
                        type: 'primary',
                        disabled: selectedIds.length <= 0,
                        icon: <CheckOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageDeliveryStatus)) {
                                securityRestriction(api, [Permission.ManageDeliveryStatus]);
                                return;
                            }

                            if (!currentTruck?.canFinishDelivery && !hasPermission(userSession.permissions, Permission.FullAccess)) {
                                error(
                                    api,
                                    'Завершение доставки',
                                    'Доставка может быть завершена только на конечном складе или администратором',
                                );
                                return;
                            }

                            modal.confirm({
                                title: `Завершить выбранные доставки?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Завершить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onFinish();
                                },
                            });
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <Filter
                display={showFilter}
                items={[
                    <Input
                        style={{ width: 130 }}
                        key='journalNumber'
                        placeholder='Журнал'
                        value={filter?.journalNumber}
                        onChange={(data) => {
                            d(setFilter({ ...filter, journalNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='number'
                        placeholder='Машина'
                        value={filter?.number}
                        onChange={(data) => {
                            d(setFilter({ ...filter, number: data.target.value }, filterContext));
                        }}
                    />,
                    <InputNumber
                        style={{ width: 130 }}
                        key='consigneeCode'
                        placeholder='ID клиента'
                        value={filter?.consigneeCode}
                        onChange={(value) => {
                            d(setFilter({ ...filter, consigneeCode: value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='markingCode'
                        placeholder='Маркировка'
                        value={filter?.markingCode}
                        onChange={(data) => {
                            d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                        }}
                    />,
                    <Input
                        style={{ width: 130 }}
                        key='awbNumber'
                        placeholder='AWB'
                        value={filter?.awbNumber}
                        onChange={(data) => {
                            d(setFilter({ ...filter, awbNumber: data.target.value }, filterContext));
                        }}
                    />,
                    <Select
                        key='status'
                        placeholder='Статус машины'
                        value={filter?.statuses}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                        options={statuses.map((s) => {
                            return { value: s.value, label: s.label };
                        })}
                    />,
                    <Select
                        key='city'
                        placeholder='Город доставки'
                        value={filter?.cityIds}
                        size='middle'
                        mode='multiple'
                        maxTagCount='responsive'
                        allowClear
                        style={{ width: 200 }}
                        onChange={(value) => d(setFilter({ ...filter, cityIds: value }, filterContext))}
                        filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
                        filterSort={(a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase())}
                        options={cities.map((s) => {
                            return { value: s.id, label: s.name };
                        })}
                    />,
                    <RangePicker
                        style={{ width: 200 }}
                        allowEmpty={[true, true]}
                        key='departure'
                        format='DD.MM.YYYY'
                        placeholder={['Отгрузка с', 'до']}
                        cellRender={(current) => {
                            return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                        }}
                        value={[
                            filter?.departureFrom ? dayjs(filter?.departureFrom) : null,
                            filter?.departureTo ? dayjs(filter?.departureTo) : null,
                        ]}
                        onChange={(value) => {
                            if (!value) {
                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            departureFrom: undefined,
                                            departureTo: undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                                return;
                            }

                            d(
                                setFilter(
                                    {
                                        ...filter,
                                        departureFrom: value[0]
                                            ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                            : undefined,
                                        departureTo: value[1]
                                            ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                            : undefined,
                                    },
                                    filterContext,
                                ),
                            );
                        }}
                    />,
                    <Form.Item key='isArchived' label='Архив' style={{ margin: 0 }}>
                        <Switch
                            checked={filter?.isArchived}
                            onChange={(value: any) => {
                                d(setFilter({ ...filter, statuses: [], isArchived: value }, filterContext));
                            }}
                        />
                    </Form.Item>,
                ]}
                onClear={() => d(setFilter(initFilter, filterContext))}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = trucks.find((t: ITruck) => t.id === selectedRowKeys[0]);

            setCurrentTruck(entity);
        } else {
            setCurrentTruck(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ITruck> = [
            {
                title: 'Машина',
                key: 'number',
                width: 150,
                render: (_, record) => {
                    return (
                        <Button variant='link' color='blue' onClick={() => setOpenDetails(true)}>
                            {record.number}
                        </Button>
                    );
                },
            },
            {
                title: 'Журнал',
                dataIndex: 'journalNumber',
                width: 100,
            },
            {
                title: 'Перевозчик',
                dataIndex: 'cargoCarrier',
                width: 200,
            },
            {
                title: 'Статус',
                width: 100,
                render: (_, record) => {
                    return (
                        <>
                            {record.status ? truckStatusLabel(record.status) : 'Не назначена'}
                            {record.trackingUrl && (
                                <Tooltip placement='topRight' title='Показать на карте'>
                                    <a className='trackingUrl' href={record.trackingUrl} target='_blank' rel='noreferrer'>
                                        <EyeFilled />
                                    </a>
                                </Tooltip>
                            )}
                        </>
                    );
                },
            },
            {
                title: 'Отгрузка',
                width: 140,
                align: 'center',
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
                render: (_, record) => {
                    return getDateTimeLocal(record.departureOn);
                },
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                width: 120,
                dataIndex: 'totalQty',
                onCell: (record) => ({
                    style: {
                        background: '#FFEE96',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Телефон водителя',
                dataIndex: 'driverPhone',
                width: 150,
            },
            {
                title: 'Достава ($)',
                width: 150,
                align: 'center',
                hidden: !hasPermission(userSession.permissions, Permission.ManageCostPriceTruck),
                onCell: () => ({
                    style: {
                        background: '#e5e5ff',
                        fontWeight: 700,
                    },
                }),
                render: (_, record) => {
                    if (!record.charteringAmount && !record.charteringCrossRate) return undefined;

                    const charteringAmountUsd = (record.charteringAmount ?? 0) * (record.charteringCrossRate ?? 0);
                    return toFinanceString(charteringAmountUsd, 2);
                },
            },
            {
                title: 'Маршрут',
                width: 750,
                key: 'route',
                render: (_, record) => {
                    return <>{record.citiesRoute?.join(' - ')} </>;
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                columns={columns}
                dataSource={trucks}
                pagination={false}
                scroll={{ y: `calc(100vh - ${showFilter ? 240 : 182}px)`, x: 'max-content' }}
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                }}
                onRow={(record: ITruck) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
            />
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {currentTruck && openDetails && (
                <Drawer
                    closable={false}
                    destroyOnHidden
                    title={`Детали доставки "${currentTruck.number}"`}
                    placement='right'
                    open={true}
                    onClose={() => setOpenDetails(false)}
                    size={860}
                    extra={
                        <Button variant='link' color='blue' style={{ fontWeight: 600, fontSize: 16 }} onClick={() => setOpenDetails(false)}>
                            Закрыть
                        </Button>
                    }
                >
                    <CargoDetails
                        path='trucks/delivery'
                        truckId={currentTruck.id || ''}
                        filter={filter}
                        countries={countries}
                        api={api}
                        serverFetch={serverFetch}
                    />
                </Drawer>
            )}

            {contextHolder}
            {notificationContextHolder}
        </>
    );
};

export default Trucks;
